import React, { useState, useEffect } from 'react';
import { integrationService } from '../services/integrationService';
import bullhornLogo from '../bullhorn_logo.png';
import ismartrecruitLogo from '../ismartrecruit_logo.png';
import recruitCrmLogo from '../recruit_crm_logo.png';

const INTEGRATIONS = [
  { type: 'ismartrecruit', name: 'iSmartRecruit', icon: ismartrecruitLogo },
  { type: 'bullhorn', name: 'Bullhorn', icon: bullhornLogo },
  { type: 'recruitcrm', name: 'Recruit CRM', icon: recruitCrmLogo }
];

export default function IntegrationList({ onConfigure }) {
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [syncStatus, setSyncStatus] = useState({ type: null, message: '', isSuccess: false });
  const [syncing, setSyncing] = useState({});

  useEffect(() => {
    loadIntegrations();
  }, []);

  const loadIntegrations = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await integrationService.getIntegrations();
      setIntegrations(data || []);
    } catch (err) {
      console.error('Failed to load integrations:', err);
      setError(err.message || 'Failed to load integrations');
    } finally {
      setLoading(false);
    }
  };

  const getIntegrationStatus = (integration) => {
    if (!integration || !integration.is_enabled) {
      return { label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    }
    if (integration.is_enabled && integration.has_credentials) {
      return { label: 'Connected', color: 'bg-green-100 text-green-800' };
    }
    if (integration.is_enabled && !integration.has_credentials) {
      return { label: 'Error', color: 'bg-red-100 text-red-800' };
    }
    return { label: 'Disabled', color: 'bg-yellow-100 text-yellow-800' };
  };

  const formatLastSync = (lastSync) => {
    if (!lastSync) return 'Never';
    try {
      const date = new Date(lastSync);
      return date.toLocaleString();
    } catch {
      return 'Unknown';
    }
  };

  const handleSync = async (type) => {
    try {
      setSyncing(prev => ({ ...prev, [type]: true }));
      setSyncStatus({ type: null, message: '', isSuccess: false });
      
      const result = await integrationService.triggerSync(type);
      
      // Show success message
      setSyncStatus({
        type,
        message: result.message || `Sync started successfully! Sync Run ID: ${result.sync_run_id || 'N/A'}`,
        isSuccess: true
      });
      
      // Reload integrations to get updated last sync time
      await loadIntegrations();
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSyncStatus({ type: null, message: '', isSuccess: false });
      }, 5000);
    } catch (err) {
      setSyncStatus({
        type,
        message: `Failed to trigger sync: ${err.message}`,
        isSuccess: false
      });
    } finally {
      setSyncing(prev => ({ ...prev, [type]: false }));
    }
  };

  const handleToggle = async (type, currentEnabled) => {
    try {
      if (currentEnabled) {
        await integrationService.disableIntegration(type);
      } else {
        await integrationService.enableIntegration(type);
      }
      await loadIntegrations();
    } catch (err) {
      alert(`Failed to ${currentEnabled ? 'disable' : 'enable'} integration: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="text-center py-8">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#00C6AE]"></div>
          <p className="mt-2 text-gray-500">Loading integrations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-red-800 text-sm">{error}</p>
        </div>
      )}
      
      {syncStatus.message && (
        <div className={`border rounded-xl p-4 ${
          syncStatus.isSuccess 
            ? 'bg-green-50 border-green-200' 
            : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-sm ${
            syncStatus.isSuccess ? 'text-green-800' : 'text-red-800'
          }`}>
            {syncStatus.message}
          </p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-semibold text-gray-900">ATS Integrations</h2>
          <p className="text-sm text-gray-500">Connect your Applicant Tracking System</p>
        </div>

        {integrations.length === 0 && !loading && (
          <div className="text-center py-6 mb-6">
            <p className="text-gray-500 mb-2">No integrations configured yet.</p>
            <p className="text-sm text-gray-400">Click "Configure" on an integration below to get started.</p>
          </div>
        )}

        <div className="space-y-4">
          {INTEGRATIONS.map((integrationDef) => {
            const integration = integrations.find(i => i.connector_type === integrationDef.type) || {
              connector_type: integrationDef.type,
              is_enabled: false,
              has_credentials: false
            };
            const status = getIntegrationStatus(integration);

            return (
              <div
                key={integrationDef.type}
                className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 flex items-center justify-center flex-shrink-0">
                      <img 
                        src={integrationDef.icon} 
                        alt={`${integrationDef.name} logo`}
                        className="max-w-full max-h-full object-contain"
                      />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{integrationDef.name}</h3>
                      <div className="flex items-center space-x-3 mt-1">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>
                          {status.label}
                        </span>
                        {integration.active_sync && (
                          <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 flex items-center space-x-1">
                            <span className="inline-block w-2 h-2 bg-blue-600 rounded-full animate-pulse"></span>
                            <span>Sync {integration.active_sync.status === 'running' ? 'Running' : 'Pending'}</span>
                          </span>
                        )}
                        {integration.last_sync && !integration.active_sync && (
                          <span className="text-xs text-gray-500">
                            Last sync: {formatLastSync(integration.last_sync)}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => onConfigure(integrationDef.type)}
                      className="px-4 py-2 text-sm font-medium text-[#00C6AE] hover:text-[#00B39E] transition-colors"
                    >
                      Configure
                    </button>
                    {integration.is_enabled && (
                      <>
                        {integration.active_sync ? (
                          <span className="px-4 py-2 text-sm font-medium text-blue-700">
                            Sync {integration.active_sync.status === 'running' ? 'Running' : 'Pending'}...
                          </span>
                        ) : (
                          <button
                            onClick={() => handleSync(integrationDef.type)}
                            disabled={syncing[integrationDef.type]}
                            className={`px-4 py-2 text-sm font-medium transition-colors ${
                              syncing[integrationDef.type]
                                ? 'text-gray-400 cursor-not-allowed'
                                : 'text-gray-700 hover:text-gray-900'
                            }`}
                          >
                            {syncing[integrationDef.type] ? 'Syncing...' : 'Sync Now'}
                          </button>
                        )}
                        <button
                          onClick={() => onConfigure(integrationDef.type, 'logs')}
                          className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors"
                        >
                          View Logs
                        </button>
                      </>
                    )}
                    {integration.has_credentials && (
                      <button
                        onClick={() => handleToggle(integrationDef.type, integration.is_enabled)}
                        className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                          integration.is_enabled
                            ? 'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                            : 'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                      >
                        {integration.is_enabled ? 'Disable' : 'Enable'}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
